import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiInstagram, FiShoppingCart, FiVideo, FiUsers } from 'react-icons/fi';
import { FaTiktok, FaYoutube, FaLinkedin, FaTwitter, FaFacebook } from 'react-icons/fa';
import './Services.css';

const Services: React.FC = () => {
  const { t } = useTranslation();

  const services = [
    {
      id: 'social',
      icon: <FiInstagram />,
      gradient: 'linear-gradient(135deg, #E4405F 0%, #833AB4 100%)',
      titleKey: 'services.social.title',
      descKey: 'services.social.desc',
      socialIcons: [
        { icon: <FiInstagram />, name: 'Instagram', color: '#E4405F' },
        { icon: <FaTiktok />, name: 'TikTok', color: '#000000' },
        { icon: <FaYoutube />, name: 'YouTube', color: '#FF0000' },
        { icon: <FaLinkedin />, name: 'LinkedIn', color: '#0A66C2' },
        { icon: <FaTwitter />, name: 'Twitter/X', color: '#1DA1F2' },
        { icon: <FaFacebook />, name: 'Facebook', color: '#1877F2' }
      ],
      infoText: 'Tüm sosyal medya platformlarında markanızı profesyonel bir şekilde temsil ediyoruz. İçerik stratejisi, düzenli paylaşımlar, topluluk yönetimi ve raporlama ile hesaplarınızı büyütüyoruz.'
    },
    {
      id: 'ecommerce',
      icon: <FiShoppingCart />,
      gradient: 'linear-gradient(135deg, #F27A1A 0%, #FF9500 100%)',
      titleKey: 'services.ecommerce.title',
      descKey: 'services.ecommerce.desc',
      platforms: [
        { name: 'Trendyol', color: '#F27A1A' },
        { name: 'Hepsiburada', color: '#FF6000' },
        { name: 'N11', color: '#7B47BC' },
        { name: 'Amazon TR', color: '#FF9900' },
        { name: 'Çiçeksepeti', color: '#E91E63' },
        { name: 'Pazarama', color: '#00BCD4' }
      ],
      infoText: 'Türkiye\'nin en büyük pazaryerlerinde mağazanızı kuruyor, ürün listeleme, optimizasyon, sipariş yönetimi ve müşteri iletişimi hizmetleri sunuyoruz.'
    },
    {
      id: 'production',
      icon: <FiVideo />,
      gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
      titleKey: 'services.ads.title',
      descKey: 'services.ads.desc',
      features: [
        { name: 'Video Prodüksiyon', icon: '🎬' },
        { name: 'Fotoğraf Çekimi', icon: '📸' },
        { name: 'Drone Çekimi', icon: '🚁' },
        { name: 'Stüdyo Hizmeti', icon: '🎙️' },
        { name: 'Post Prodüksiyon', icon: '🎨' },
        { name: 'Animasyon', icon: '✨' }
      ],
      infoText: 'Profesyonel ekipmanlar ve deneyimli ekibimizle markanız için etkileyici görsel içerikler üretiyoruz. Reklamlarınız için A\'dan Z\'ye prodüksiyon desteği sağlıyoruz.'
    },
    {
      id: 'models',
      icon: <FiUsers />,
      gradient: 'linear-gradient(135deg, #00D4FF 0%, #7B68EE 100%)',
      titleKey: 'services.models.title',
      descKey: 'services.models.desc',
      languages: [
        { name: 'Türkçe', flag: '🇹🇷' },
        { name: 'İngilizce', flag: '🇬🇧' },
        { name: 'Almanca', flag: '🇩🇪' },
        { name: 'Rusça', flag: '🇷🇺' },
        { name: 'Arapça', flag: '🇸🇦' },
        { name: 'Diğer', flag: '🌍' }
      ],
      infoText: 'Farklı dillerde konuşan profesyonel modellerimizle uluslararası pazarlara hitap eden reklam içerikleri oluşturuyoruz. Çeşitli profil ve yaş gruplarında model seçenekleri sunuyoruz.'
    }
  ];

  return (
    <section className="services section" id="services">
      <div className="container">
        <motion.div
          className="services-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="section-title">{t('services.title')}</h2>
          <p className="section-subtitle">{t('services.subtitle')}</p>
        </motion.div>

        <div className="services-list">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className="service-row"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {/* Sol taraf - İkonlar/Platformlar */}
              <div className="service-left">
                <div className="service-icon-header" style={{ background: service.gradient }}>
                  {service.icon}
                </div>
                <h3 className="service-title">{t(service.titleKey)}</h3>
                
                <div className="service-items">
                  {service.socialIcons && service.socialIcons.map((social, idx) => (
                    <div key={idx} className="service-item social-item" style={{ '--item-color': social.color } as React.CSSProperties}>
                      <span className="item-icon">{social.icon}</span>
                      <span className="item-name">{social.name}</span>
                    </div>
                  ))}
                  
                  {service.platforms && service.platforms.map((platform, idx) => (
                    <div key={idx} className="service-item platform-item" style={{ '--item-color': platform.color } as React.CSSProperties}>
                      <span className="item-name">{platform.name}</span>
                    </div>
                  ))}
                  
                  {service.features && service.features.map((feature, idx) => (
                    <div key={idx} className="service-item feature-item">
                      <span className="item-emoji">{feature.icon}</span>
                      <span className="item-name">{feature.name}</span>
                    </div>
                  ))}
                  
                  {service.languages && service.languages.map((lang, idx) => (
                    <div key={idx} className="service-item language-item">
                      <span className="item-flag">{lang.flag}</span>
                      <span className="item-name">{lang.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sağ taraf - Bilgi yazısı */}
              <div className="service-right">
                <div className="service-info-card">
                  <p className="service-info-text">{service.infoText}</p>
                  <a 
                    href="https://wa.me/905337757181" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="service-cta-btn"
                  >
                    Teklif Al
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
